import { ProblemFormatOptions, SPIRAL_SECTION_FORMATS } from "../Constants.ts";

/**
 * Encodes and decodes the spiral-review section list for the URL (ADR 0013).
 *
 * The section list is the first setting that is not a scalar, so it gets a
 * short code per format - `sr=mf:8,fr:4,pv:3` - rather than the format names,
 * which carry spaces and an ampersand. Decoding is defensive: an unknown code,
 * a bad count, or a malformed pair is dropped, not thrown.
 */

const CODE_TO_FORMAT: Record<string, ProblemFormatOptions> = {
    mf: ProblemFormatOptions.MathFacts,
    fr: ProblemFormatOptions.Fractions,
    mo: ProblemFormatOptions.Money,
    dc: ProblemFormatOptions.Decimals,
    pv: ProblemFormatOptions.PlaceValue,
    rc: ProblemFormatOptions.RoundingComparing,
    tt: ProblemFormatOptions.TellingTime,
};

const FORMAT_TO_CODE: Partial<Record<ProblemFormatOptions, string>> = Object.fromEntries(
    Object.entries(CODE_TO_FORMAT).map(([code, format]) => [format, code])
);

export function encodeSpiralSections(
    sections: { format: ProblemFormatOptions; count: number }[]
): string {
    return sections
        .filter((section) => FORMAT_TO_CODE[section.format] && section.count > 0)
        .map((section) => `${FORMAT_TO_CODE[section.format]}:${section.count}`)
        .join(",");
}

export function decodeSpiralSections(
    value: string | null
): { format: ProblemFormatOptions; count: number }[] {
    if (!value) return [];
    return value
        .split(",")
        .map((pair) => {
            const [code, rawCount] = pair.split(":");
            const format = CODE_TO_FORMAT[code?.trim()];
            const count = parseInt(rawCount, 10);
            if (!format || !SPIRAL_SECTION_FORMATS.includes(format)) return null;
            if (!Number.isFinite(count) || count < 1 || count > 30) return null;
            return { format, count };
        })
        .filter((section): section is { format: ProblemFormatOptions; count: number } => section !== null);
}
